// src/pages/blogadmin.js
import React, { useState, useEffect } from "react";
import MDEditor from "@uiw/react-md-editor";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";

import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

const storage = getStorage();

/* ---------------------- KOMPRESJA OBRAZU --------------------------- */
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => (img.src = e.target.result);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const maxDim = 2000;

      let w = img.width;
      let h = img.height;

      if (Math.max(w, h) > maxDim) {
        const ratio = maxDim / Math.max(w, h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }

      canvas.width = Math.max(1, w);
      canvas.height = Math.max(1, h);

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Compression failed"));
          const ext = blob.type === "image/webp" ? ".webp" : ".jpg";
          const compressedFile = new File([blob], `${Date.now()}${ext}`, {
            type: blob.type,
          });
          resolve(compressedFile);
        },
        "image/webp",
        0.75
      );
    };
    img.onerror = reject;
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/* ------------------- GENERATOR SLUGA ------------------- */
const makeSlug = (title) =>
  title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9ąćęłńóśźż ]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

function BlogAdmin() {
  const [posts, setPosts] = useState([]);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [manualSlug, setManualSlug] = useState(false);

  const [coverUrl, setCoverUrl] = useState("");
  const [content, setContent] = useState("");
  const [gallery, setGallery] = useState([]);

  const [createdAt, setCreatedAt] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  /* ---------------------- AUTO SLUG ---------------------- */
  useEffect(() => {
    if (!manualSlug) {
      setSlug(makeSlug(title));
    }
  }, [title, manualSlug]);

  /* ---------------------- POBIERANIE WPISÓW -------------------------- */
  const fetchPosts = async () => {
    const querySnapshot = await getDocs(collection(db, "blog"));
    const entries = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setPosts(entries);
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  /* ---------------------- UPLOAD OKŁADKI ----------------------------- */
  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file);
      const fileRef = ref(storage, `blog-covers/${Date.now()}-${file.name}`);
      await uploadBytes(fileRef, compressed);
      const url = await getDownloadURL(fileRef);
      setCoverUrl(url);
    } catch (err) {
      console.error(err);
      setMessage("❌ Błąd uploadu okładki.");
    }
  };

  /* ---------------------- UPLOAD GALERII ----------------------------- */
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    const uploaded = [];

    for (const f of files) {
      try {
        const compressed = await compressImage(f);
        const fileRef = ref(storage, `blog-gallery/${Date.now()}-${f.name}`);
        await uploadBytes(fileRef, compressed);
        const url = await getDownloadURL(fileRef);
        uploaded.push(url);
      } catch (err) {
        console.error(err);
      }
    }

    setGallery((prev) => [...prev, ...uploaded]);
  };

  /* ---------------------- ZAPIS / AKTUALIZACJA ------------------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !slug || !content) {
      setMessage("⚠️ Uzupełnij tytuł, slug i treść.");
      return;
    }

    if (!createdAt) {
      setMessage("⚠️ Wybierz datę publikacji.");
      return;
    }

    setLoading(true);

    try {
      const timestamp = Timestamp.fromDate(new Date(createdAt));

      if (editingId) {
        const refDoc = doc(db, "blog", editingId);
        await updateDoc(refDoc, {
          title,
          slug,
          coverUrl,
          content,
          gallery,
          createdAt: timestamp,
        });

        setMessage("✅ Wpis zaktualizowany!");
      } else {
        await addDoc(collection(db, "blog"), {
          title,
          slug,
          coverUrl,
          content,
          gallery,
          createdAt: timestamp,
        });

        setMessage("✅ Nowy wpis dodany!");
      }

      // reset
      setTitle("");
      setSlug("");
      setManualSlug(false);
      setCoverUrl("");
      setContent("");
      setGallery([]);
      setCreatedAt("");
      setEditingId(null);

      fetchPosts();
    } catch (err) {
      console.error(err);
      setMessage("❌ Błąd przy zapisie!");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------- EDYCJA WPISU ------------------------------- */
  const handleEdit = (post) => {
    setEditingId(post.id);
    setTitle(post.title);
    setSlug(post.slug);
    setManualSlug(true);
    setCoverUrl(post.coverUrl || "");
    setContent(post.content);
    setGallery(post.gallery || []);

    // konwersja Timestamp → datetime-local
    if (post.createdAt?.seconds) {
      const date = new Date(post.createdAt.seconds * 1000);
      setCreatedAt(date.toISOString().slice(0, 16));
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ---------------------- RENDER ------------------------------------- */

  return (
    <div className="container mx-auto p-6 max-w-4xl" data-color-mode="light">
      <h1 className="text-3xl font-bold mb-4">📘 Panel admina – Blog</h1>

      {message && (
        <div className="mb-3 p-2 bg-gray-100 border border-gray-300 rounded">
          {message}
        </div>
      )}

      {/* FORMULARZ */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 mb-10">
        {/* Tytuł */}
        <input
          type="text"
          placeholder="Tytuł wpisu"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          className="border p-2 rounded"
        />

        {/* Slug */}
        <input
          type="text"
          placeholder="Slug"
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            setManualSlug(true);
          }}
          className="border p-2 rounded"
        />

        {/* Data publikacji */}
        <label className="font-semibold mt-3">Data publikacji</label>
        <input
          type="datetime-local"
          value={createdAt}
          onChange={(e) => setCreatedAt(e.target.value)}
          className="border p-2 rounded"
        />

        {/* OKŁADKA */}
        <label className="font-semibold mt-3">Zdjęcie okładkowe</label>
        <input type="file" accept="image/*" onChange={handleCoverUpload} />
        {coverUrl && (
          <img src={coverUrl} alt="Okładka" className="w-40 rounded mt-2 border" />
        )}

        {/* GALERIA */}
        <label className="font-semibold mt-3">Galeria zdjęć</label>
        <input type="file" multiple accept="image/*" onChange={handleGalleryUpload} />

        {gallery.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            {gallery.map((g, i) => (
              <img key={i} alt={g} src={g} className="w-full rounded border" />
            ))}
          </div>
        )}

        {/* Treść */}
        <label className="font-semibold mt-3">Treść (Markdown)</label>
        <MDEditor value={content} onChange={setContent} height={400} preview="live" />

        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded p-2 mt-4"
        >
          {loading
            ? "Zapisywanie..."
            : editingId
            ? "💾 Zaktualizuj wpis"
            : "➕ Dodaj nowy wpis"}
        </button>
      </form>

      {/* LISTA WPISÓW */}
      <div className="mt-6">
        <h2 className="text-2xl font-semibold mb-3">📄 Istniejące wpisy</h2>
        {posts.length === 0 && <p>Brak wpisów w bazie.</p>}

        <ul className="flex flex-col gap-2">
          {posts.map((post) => (
            <li
              key={post.id}
              className="p-3 border rounded flex justify-between items-center bg-white"
            >
              <div>
                <p className="font-bold">{post.title}</p>
                <p className="text-sm text-gray-600">{post.slug}</p>
              </div>

              <button
                onClick={() => handleEdit(post)}
                className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded"
              >
                ✏️ Edytuj
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default BlogAdmin;
